import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLanguage } from '../contexts/LanguageContext';

const InternalNotifications = () => {
    const { language } = useLanguage();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);
    const { socket } = useWebSocket();
    
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    // Fetch notifications and unread count
    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/notifications`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
                const unread = data.filter(n => !n.is_read).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/notifications/unread-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/notifications/${notificationId}/read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/notifications/mark-all-read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Listen for real-time notifications via WebSocket
    useEffect(() => {
        if (socket) {
            socket.on('package_status_update', (data) => {
                // Add new notification to the list
                const newNotification = {
                    id: data.id,
                    subject: data.subject,
                    message: data.message,
                    entity_type: data.entity_type,
                    entity_id: data.entity_id,
                    created_at: data.created_at,
                    is_read: false
                };
                setNotifications(prev => [newNotification, ...prev]);
                setUnreadCount(prev => prev + 1);

                // Show browser notification if permitted (not available on iOS)
                if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                    new Notification(data.subject, {
                        body: data.message,
                        icon: '/logo.png'
                    });
                }
            });

            socket.on('package_created', (data) => {
                toast.success(
                    language === 'en'
                        ? `New package ${data.oce_code}`
                        : `Nouveau colis ${data.oce_code}`,
                    {
                        description: `${data.sender_name} → ${data.recipient_name}`,
                    }
                );
                fetchUnreadCount();
            });
        }

        return () => {
            if (socket) {
                socket.off('package_status_update');
                socket.off('package_created');
            }
        };
    }, [socket, language]);

    // Load notifications on mount
    useEffect(() => {
        fetchNotifications();
        
        // Request notification permission (not available on iOS Safari)
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission();
        }
        
        // Poll for unread count every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const getTimeAgo = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        const m = Math.floor(seconds / 60);
        const h = Math.floor(seconds / 3600);
        const d = Math.floor(seconds / 86400);
        if (language === 'en') {
            if (seconds < 60) return 'Just now';
            if (seconds < 3600) return `${m}min ago`;
            if (seconds < 86400) return `${h}h ago`;
            return `${d}d ago`;
        }
        if (seconds < 60) return 'À l\'instant';
        if (seconds < 3600) return `Il y a ${m}min`;
        if (seconds < 86400) return `Il y a ${h}h`;
        return `Il y a ${d}j`;
    };

    const getNotificationIcon = (subject) => {
        if (subject.includes('📦')) return '📦';
        if (subject.includes('✅')) return '✅';
        if (subject.includes('✈️')) return '✈️';
        if (subject.includes('📍')) return '📍';
        if (subject.includes('🎯')) return '🎯';
        return '🔔';
    };

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell Icon with Badge */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification Panel */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">{language === 'en' ? 'Notifications' : 'Notifications'}</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-sm text-accent hover:text-blue-800 flex items-center gap-1"
                                    title={language === 'en' ? 'Mark all as read' : 'Tout marquer comme lu'}
                                >
                                    <CheckCheck className="h-4 w-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="overflow-y-auto flex-1">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                <p>{language === 'en' ? 'No notifications' : 'Aucune notification'}</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                            !notification.is_read ? 'bg-accent/10' : ''
                                        }`}
                                        onClick={() => !notification.is_read && markAsRead(notification.id)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="text-2xl flex-shrink-0">
                                                {getNotificationIcon(notification.subject)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 mb-1">
                                                    {notification.subject}
                                                </p>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-gray-500">
                                                        {getTimeAgo(notification.created_at)}
                                                    </span>
                                                    {!notification.is_read && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                markAsRead(notification.id);
                                                            }}
                                                            className="text-xs text-accent hover:text-blue-800 flex items-center gap-1"
                                                        >
                                                            <Check className="h-3 w-3" />
                                                            Marquer lu
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalNotifications;
